/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: src/components/auth/CustomSignUp.tsx
 * Release: v2.0.0 - Registrazione completa con video intro Coronatek V2
 * Data: Dicembre 2024
 */

import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthProvider';

import coronatekIntro from '../auth/asset/Intro_Coronatek.mp4';
import coronatekLogo from '../auth/asset/Coronatek_Logo_92dpi_200x88.webp';
import dialogicomLogo from '../auth/asset/Dialogicom_432x144.webp';
import bgGradient from '../auth/asset/bg-gradient.jpg';
import GoogleSignInButton from './GoogleSignInButton';

const CustomSignUp: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [surname, setSurname] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [skipVideo, setSkipVideo] = useState(false);
  const [info, setInfo] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const navigate = useNavigate();
  const { register } = useAuth();

  // Controlla se saltare il video
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('skipVideo') === 'true') {
      setSkipVideo(true);
      setShowForm(true);
    }
  }, []);

  const handleVideoEnd = () => {
    setShowForm(true);
  };

  const handleVideoHover = () => {
    if (videoRef.current) {
      videoRef.current.play();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setInfo(null);
    setErrorMsg(null);
    
    // Validazioni
    if (password !== confirmPassword) {
      setErrorMsg('Le password non coincidono');
      setIsLoading(false);
      return;
    }

    if (password.length < 8) {
      setErrorMsg('La password deve essere di almeno 8 caratteri');
      setIsLoading(false);
      return;
    }

    try {
      // 🚀 LOGGING DETTAGLIATO PER DEBUG
      console.log('🚀 [FRONTEND] Inizio registrazione...');
      console.log('📝 [FRONTEND] Dati da inviare:', { email, password, name, surname });
      console.log('⏰ [FRONTEND] Timestamp:', new Date().toISOString());
      console.log('🆔 [FRONTEND] Request ID:', Math.random().toString(36).substr(2, 9));
      
      const result = await register(email, password, name, surname);
      
      if (result.success) {
        console.log('✅ [FRONTEND] Registrazione completata con successo:', result);
        setInfo(`Registrazione completata! Controlla la tua email (${email}) per confermare l'account.`);
        const registeredEmail = email;
        setEmail('');
        setPassword('');
        setConfirmPassword('');
        setName('');
        setSurname('');
        setErrorMsg(null);
        
        // Redirect informativo alla pagina di verifica email
        navigate(`/verify-email?pending=1&email=${encodeURIComponent(registeredEmail)}`);
      } else {
        console.error('❌ [FRONTEND] Registrazione fallita:', result.error);
        setErrorMsg(result.error || 'Registrazione non riuscita');
      }
    } catch (error) {
      console.error('❌ [FRONTEND] Errore durante la registrazione:', error);
      if (error instanceof Error) {
        console.error('❌ [FRONTEND] Stack trace:', error.stack);
        console.error('❌ [FRONTEND] Error message:', error.message);
      }
      console.error('❌ [FRONTEND] Error type:', typeof error);
      setErrorMsg('Errore di connessione. Riprova più tardi.');
    } finally {
      setIsLoading(false);
    }
  };

  // Video intro fullscreen se non saltato
  if (!showForm && !skipVideo) {
    return (
      <div className="fixed inset-0 z-50">
        <video
          ref={videoRef}
          src={coronatekIntro}
          autoPlay
          muted
          playsInline
          onEnded={handleVideoEnd}
          onMouseEnter={handleVideoHover}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white">
            <h1 className="text-4xl font-bold mb-4">Coronatek</h1>
            <p className="text-xl">Sistema di Gestione Riparazioni</p>
            <button
              onClick={() => setShowForm(true)}
              className="mt-8 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Salta Video
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 bg-cover text-white overflow-auto"
      style={{ backgroundImage: `url(${bgGradient})` }}
    >
      {/* Header */}
      <div className="absolute top-0 left-0 right-0 flex justify-between items-center px-6 py-3 bg-slate-800 z-10 h-32">
        <img src={coronatekLogo} alt="Coronatek Logo" className="h-16 drop-shadow-xl" />
        <div className="text-right">
          <h1 className="text-3xl sm:text-5xl font-bold text-green-400 mb-1 px-2">Registrati</h1>
          <p className="text-base sm:text-lg text-gray-400 px-2">Unisciti a TurboManager</p>
        </div>
      </div>

      <div className="min-h-screen flex flex-col md:flex-row items-start gap-8 px-4 pt-40 pb-4">
        {/* Left: Sign-Up form */}
        <div className="w-full md:w-1/2 animate-slide-in-left transition-all duration-1000 ease-out">
          <div className="bg-slate-800 rounded-xl shadow-2xl p-6 space-y-4">
            <div className="text-center mb-2">
              <a href="/login?skipVideo=true" className="text-sm text-gray-400 hover:text-white">Hai già un account? Accedi</a>
            </div>

            <GoogleSignInButton className="w-full" label="Registrati con Google" />
            <div className="text-center text-gray-400">oppure</div>

            <form onSubmit={handleSubmit} className="space-y-3">
              <input
                type="text"
                placeholder="Nome"
                value={name}
                onChange={e => setName(e.target.value)}
                className="w-full px-3 py-2 bg-slate-900 rounded text-white border border-slate-700"
                required
                disabled={isLoading}
              />
              <input
                type="text"
                placeholder="Cognome"
                value={surname}
                onChange={e => setSurname(e.target.value)}
                className="w-full px-3 py-2 bg-slate-900 rounded text-white border border-slate-700"
                required
                disabled={isLoading}
              />
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full px-3 py-2 bg-slate-900 rounded text-white border border-slate-700"
                required
                disabled={isLoading}
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full px-3 py-2 bg-slate-900 rounded text-white border border-slate-700"
                required
                disabled={isLoading}
              />
              <input
                type="password"
                placeholder="Conferma Password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                className="w-full px-3 py-2 bg-slate-900 rounded text-white border border-slate-700"
                required
                disabled={isLoading}
              />
              {errorMsg && <p className="text-red-400 text-sm">{errorMsg}</p>}
              {info && <p className="text-green-400 text-sm">{info}</p>}
              <button
                type="submit"
                disabled={isLoading}
                className="w-full bg-green-600 py-2 rounded hover:bg-green-500 transition-transform duration-200 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Registrazione in corso...' : 'Registrati'}
              </button>
            </form>

            <div className="pt-4 text-center">
              <a href="https://dialogicom.com" target="_blank" rel="noopener noreferrer">
                <img src={dialogicomLogo} alt="Dialogicom Logo" className="mx-auto w-[200px]" />
              </a>
            </div>
          </div>
        </div>

        {/* Right: Video + Text */}
        <div className="w-full md:w-1/2 flex flex-col justify-between animate-slide-in-up transition-all duration-1000 ease-out">
          <div className="bg-slate-800 rounded-xl shadow-2xl p-6 w-full aspect-video overflow-hidden">
            <video
              ref={videoRef}
              src={coronatekIntro}
              muted
              preload={skipVideo ? 'auto' : 'metadata'}
              autoPlay={skipVideo}
              onMouseOver={handleVideoHover}
              className="w-full h-full rounded-lg shadow-lg object-cover"
            />
          </div>
          <div className="bg-slate-800 rounded-xl shadow-2xl p-6 w-full mt-2 animate-slide-in-up transition-all duration-1000 ease-out delay-500">
            <p className="text-gray-300 text-sm leading-relaxed mb-0">
              Con <span className="font-semibold text-green-400">TurboManager</span>, Coronatek porta l&apos;officina nell&apos;era dell&apos;<strong>Intelligenza Artificiale</strong>: un gestionale smart, intuitivo e basato sulle tecnologie più recenti, creato per semplificare il lavoro e aumentare l&apos;efficienza.
            </p>
            <p className="text-gray-300 text-sm leading-relaxed">
              Tutto questo per offrire ai clienti un servizio sempre più <strong>preciso, rapido e professionale</strong>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomSignUp;
